import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NotificationService } from '../notification/notification.service';
import { AuthService } from '../auth/auth.service';
import { describeCron } from './foxi-cron';

interface FoxiAutomation {
  id: number;
  title: string;
  instruction: string;
  cron: string;
  enabled: boolean;
  ownerUserId: number | null;
  ownerName: string | null;
  nextRunAt: string | null;
  lastRunAt: string | null;
  lastResult: string | null;
  createdAt: string;
}

interface FoxiOverview {
  botUserId: number | null;
  ready: boolean;
  enabled: boolean;
  hasApiKey: boolean;
  model: string;
  todayCostUsd: number;
  monthCostUsd: number;
  dailyBudgetUsd: number;
  automations: FoxiAutomation[];
  memories: { id: number; text: string; createdAt: string; createdByUserId: number | null }[];
}

/** Foxi 🦊 oldala: mit tud, időzített feladatai, emlékei — és egy gomb, ami a vele folytatott chatbe visz. */
@Component({
  selector: 'app-foxi',
  templateUrl: './foxi.component.html',
  styleUrl: './foxi.component.scss',
})
export class FoxiComponent implements OnInit, OnDestroy {
  loading = true;
  loadFailed = false;
  data: FoxiOverview | null = null;
  expanded: Record<number, boolean> = {};
  busy: Record<number, boolean> = {};

  readonly examples = [
    'Mennyi doboz és buborékfólia van még, és meddig elég?',
    'Mit kell most beszerezni, és mennyibe kerül?',
    'Van olyan ticket, amit 20 órája senki nem vett fel?',
    'Melyik csomag áll egy helyben több mint 7 napja?',
    'Hány rendelés jött ezen a héten boltonként?',
    'Minden szerdán 9-kor írj Emesének, hogy nézze meg a doboz és a bubifólia készletét.',
    'Óránként nézd meg, van-e 20 órája felvetlen ticket, és ha igen, szólj Csabinak.',
    'Jegyezd meg: a dobozokat a Packaging Kft-től rendeljük, általában 500 db-ot.',
  ];

  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(private http: HttpClient, private notification: NotificationService, private auth: AuthService) {}

  ngOnInit(): void {
    this.load();
    this.timer = setInterval(() => this.load(true), 60_000);
  }

  ngOnDestroy(): void {
    if (this.timer) clearInterval(this.timer);
  }

  load(silent = false): void {
    if (!silent) this.loading = true;
    this.http.get<FoxiOverview>('agent/overview').subscribe({
      next: (data) => {
        this.data = data;
        this.loading = false;
        this.loadFailed = false;
      },
      error: () => {
        this.loading = false;
        this.loadFailed = !this.data;
      },
    });
  }

  get chatLink(): string | null {
    return this.data?.botUserId ? `/chat/u-${this.data.botUserId}` : null;
  }

  get statusLabel(): string {
    if (!this.data) return '';
    if (!this.data.hasApiKey) return 'Még nincs bekapcsolva (API kulcs hiányzik)';
    if (!this.data.enabled) return 'Kikapcsolva';
    return 'Ébren van és dolgozik';
  }

  get isAdmin(): boolean {
    const user = this.auth.user;
    return ((user?.roleNames || user?.roles || []) as string[]).includes('admin');
  }

  canManage(a: FoxiAutomation): boolean {
    return this.isAdmin || a.ownerUserId === this.auth.user?.id;
  }

  schedule(cron: string): string {
    return describeCron(cron);
  }

  toggle(a: FoxiAutomation): void {
    this.busy[a.id] = true;
    this.http.patch(`agent/automations/${a.id}`, { enabled: !a.enabled }).subscribe({
      next: () => {
        this.busy[a.id] = false;
        this.load(true);
      },
      error: (e) => {
        this.busy[a.id] = false;
        this.notification.show(e?.error?.message || 'Nem sikerült módosítani.', 'danger');
      },
    });
  }

  runNow(a: FoxiAutomation): void {
    this.busy[a.id] = true;
    this.notification.show(`Foxi nekiállt: „${a.title}"…`, 'info');
    this.http.post<{ result: string }>(`agent/automations/${a.id}/run`, {}).subscribe({
      next: () => {
        this.busy[a.id] = false;
        this.expanded[a.id] = true;
        this.notification.show('Kész — az eredmény a feladatnál látható.', 'success');
        this.load(true);
      },
      error: (e) => {
        this.busy[a.id] = false;
        this.notification.show(e?.error?.message || 'A futtatás nem sikerült.', 'danger');
      },
    });
  }

  remove(a: FoxiAutomation): void {
    if (!confirm(`Biztosan törlöd: „${a.title}"?`)) return;
    this.http.delete(`agent/automations/${a.id}`).subscribe({
      next: () => this.load(true),
      error: (e) => this.notification.show(e?.error?.message || 'Nem sikerült törölni.', 'danger'),
    });
  }

  forget(id: number): void {
    if (!confirm('Elfelejtse Foxi ezt?')) return;
    this.http.delete(`agent/memories/${id}`).subscribe({
      next: () => this.load(true),
      error: () => this.notification.show('Nem sikerült törölni.', 'danger'),
    });
  }

  copy(text: string): void {
    navigator.clipboard?.writeText(text).then(
      () => this.notification.show('Kimásolva — illeszd be Foxi chatjébe.', 'success'),
      () => undefined,
    );
  }
}
